import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { COACHES, TOPIC_CATEGORIES, SESSION_LENGTHS } from "@/data/coaches";
import CoachAvatar from "@/components/CoachAvatar";
import { ArrowLeft, Search, Check } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CoachingSetupPage() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetTopic = searchParams.get("topic") || "";
  const capscanId = searchParams.get("capscan_id") || "";
  const capscanGap = searchParams.get("gap") || presetTopic;

  const [step, setStep] = useState(1);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [topic, setTopic] = useState(presetTopic);
  const [customTopic, setCustomTopic] = useState("");
  const [topicSearch, setTopicSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(TOPIC_CATEGORIES[0].id);
  const [length, setLength] = useState(30);
  const [confidenceStart, setConfidenceStart] = useState(5);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  // If preset topic from CapScan link, advance to step 1 with coach selection
  const finalTopic = customTopic.trim() || topic;
  const freeAvailable = user && !user.coaching_free_session_used;
  const lengthRow = SESSION_LENGTHS.find((l) => l.minutes === length);
  const cost = lengthRow?.tokens || 5;

  const filteredCategories = useMemo(() => {
    if (!topicSearch.trim()) return TOPIC_CATEGORIES;
    const q = topicSearch.toLowerCase();
    return TOPIC_CATEGORIES.map((c) => ({
      ...c,
      topics: c.topics.filter((t) => t.toLowerCase().includes(q)),
    })).filter((c) => c.topics.length > 0);
  }, [topicSearch]);

  const noMatch = topicSearch.trim() && filteredCategories.length === 0;

  const startSession = async () => {
    setError("");
    setStarting(true);
    try {
      const r = await axios.post(`${API}/api/coaching/start`, {
        coach_id: selectedCoach.id,
        topic: finalTopic,
        length_minutes: length,
        capscan_id: capscanId,
        capscan_gap: capscanGap,
        confidence_start: confidenceStart,
      }, { withCredentials: true });
      await refreshUser();
      navigate(`/coaching/session/${r.data.session_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to start session");
    } finally {
      setStarting(false);
    }
  };

  if (loading || !user) return <div className="min-h-screen bg-cream flex items-center justify-center pt-16"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-cream pt-24 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/coaching" className="inline-flex items-center gap-2 text-navy/60 hover:text-gold font-dm text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Coaching
        </Link>

        {capscanGap && (
          <div className="bg-gold/10 border-l-4 border-gold rounded-r-lg p-4 mb-6" data-testid="capscan-deeplink-banner">
            <p className="font-dm text-navy text-sm">Coaching on your CapScan gap: <strong>{capscanGap}</strong></p>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-dm font-bold text-sm ${step >= s ? "bg-gold text-navy" : "bg-white text-navy/40 border-2 border-navy/10"}`}>{step > s ? <Check className="w-5 h-5" /> : s}</div>
              {s < 4 && <div className={`h-1 flex-1 mx-2 rounded ${step > s ? "bg-gold" : "bg-navy/10"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Step 1: Coach */}
          {step === 1 && (
            <div data-testid="coaching-step-1">
              <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Step 1 — Choose your coach</h2>
              <p className="font-dm text-navy/60 text-sm mb-6">Each coach brings a distinct presence and methodology.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {COACHES.map((c) => (
                  <button key={c.id} onClick={() => setSelectedCoach(c)} data-testid={`select-coach-${c.id}`}
                    className={`text-left rounded-xl p-4 border-2 transition-all ${selectedCoach?.id === c.id ? "border-gold bg-gold/5" : "border-navy/10 hover:border-gold/50"}`}>
                    <CoachAvatar coach={c} size={56} className="mb-2" />
                    <h3 className="font-playfair text-navy text-base font-bold leading-tight">{c.name}</h3>
                    <p className="font-dm text-navy/50 text-[10px] mb-2">{c.flag} {c.origin} · {c.level}</p>
                    <p className="font-dm text-navy/70 text-xs italic">"{c.signature.length > 80 ? c.signature.slice(0, 80) + '…' : c.signature}"</p>
                  </button>
                ))}
              </div>
              {selectedCoach && (
                <div className="mt-6 bg-cream rounded-xl p-5" data-testid="coach-bio-panel">
                  <h3 className="font-playfair text-navy font-bold mb-2">{selectedCoach.name} — {selectedCoach.level}</h3>
                  <p className="font-dm text-navy/70 text-sm mb-2"><strong>Style:</strong> {selectedCoach.style}</p>
                  <p className="font-dm text-navy/70 text-sm mb-3"><strong>Best for:</strong> {selectedCoach.best_for}</p>
                  <p className="font-dm text-navy/60 text-xs uppercase font-bold mb-1">How {selectedCoach.name.split(" ")[0]} opens:</p>
                  <ul className="font-dm text-navy/70 text-xs italic list-disc pl-5">
                    {selectedCoach.openings.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </div>
              )}
              <button onClick={() => setStep(2)} disabled={!selectedCoach} data-testid="step1-next" className="mt-6 w-full bg-navy text-gold font-dm font-bold py-3 rounded-lg hover:bg-navy/90 disabled:opacity-50">Next</button>
            </div>
          )}

          {/* Step 2: Topic */}
          {step === 2 && (
            <div data-testid="coaching-step-2">
              <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Step 2 — What would you like to explore?</h2>
              <p className="font-dm text-navy/60 text-sm mb-4">Pick a topic or describe your own.</p>
              <div className="relative mb-4">
                <Search className="w-4 h-4 absolute left-3 top-3 text-navy/40" />
                <input value={topicSearch} onChange={(e) => setTopicSearch(e.target.value)} placeholder="Search for a topic..." data-testid="topic-search"
                  className="w-full border border-navy/20 rounded-lg pl-9 pr-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
              </div>

              {!noMatch ? (
                <>
                  <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                    {filteredCategories.map((c) => (
                      <button key={c.id} onClick={() => setActiveCategory(c.id)} data-testid={`topic-cat-${c.id}`}
                        className={`whitespace-nowrap font-dm text-xs px-3 py-1.5 rounded-full font-bold ${activeCategory === c.id ? "bg-navy text-gold" : "bg-navy/5 text-navy/60 hover:bg-navy/10"}`}>
                        {c.emoji} {c.name}
                      </button>
                    ))}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                    {(filteredCategories.find((c) => c.id === activeCategory)?.topics || filteredCategories[0]?.topics || []).map((t) => (
                      <button key={t} onClick={() => { setTopic(t); setCustomTopic(""); }} data-testid={`topic-${t.replace(/\s/g, "-").toLowerCase()}`}
                        className={`text-left px-3 py-2 rounded-lg font-dm text-sm border ${topic === t ? "border-gold bg-gold/10 text-navy font-bold" : "border-navy/10 text-navy/70 hover:border-gold/50"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="font-dm text-navy/60 text-sm py-4">No topics match "{topicSearch}".</p>
              )}

              <div className="mt-4 pt-4 border-t border-navy/10">
                <label className="font-dm text-navy/70 text-xs font-semibold block mb-1">Don't see your topic? Describe it in your own words.</label>
                <input value={customTopic} onChange={(e) => { setCustomTopic(e.target.value); if (e.target.value) setTopic(""); }} placeholder="e.g. Preparing for a hard 1:1 with my CEO" data-testid="custom-topic"
                  className="w-full border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(1)} className="font-dm text-navy/60 px-4 py-3 hover:text-navy">← Back</button>
                <button onClick={() => setStep(3)} disabled={!finalTopic} data-testid="step2-next" className="flex-1 bg-navy text-gold font-dm font-bold py-3 rounded-lg disabled:opacity-50">Next</button>
              </div>
            </div>
          )}

          {/* Step 3: Length */}
          {step === 3 && (
            <div data-testid="coaching-step-3">
              <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Step 3 — Session length</h2>
              <p className="font-dm text-navy/60 text-sm mb-6">Choose how long you want to spend together. Sessions never exceed 60 minutes.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {SESSION_LENGTHS.map((l) => {
                  const isFreeOption = freeAvailable && l.minutes === 30;
                  return (
                    <button key={l.minutes} onClick={() => setLength(l.minutes)} data-testid={`length-${l.minutes}`}
                      className={`relative rounded-xl p-4 border-2 transition-all ${length === l.minutes ? "border-gold bg-gold/5" : "border-navy/10 hover:border-gold/50"}`}>
                      {isFreeOption && <span className="absolute -top-2 right-2 bg-green-500 text-white font-dm text-[10px] font-bold px-2 py-0.5 rounded-full">FREE</span>}
                      <p className="font-playfair text-navy text-2xl font-bold mb-1">{l.minutes}</p>
                      <p className="font-dm text-navy/50 text-xs mb-2">minutes</p>
                      <p className={`font-dm text-xs font-bold ${freeAvailable ? "text-navy/40 line-through" : "text-gold"}`}>{l.tokens} assessment tokens</p>
                    </button>
                  );
                })}
              </div>

              {/* Confidence-start slider */}
              <div className="bg-cream rounded-xl p-5 mt-6" data-testid="confidence-start-section">
                <p className="font-dm text-navy font-bold text-sm mb-1">How confident are you about this topic right now?</p>
                <p className="font-dm text-navy/60 text-xs mb-4">Quick check-in. We'll measure your shift at the end of the session.</p>
                <input type="range" min={1} max={10} value={confidenceStart}
                  onChange={(e) => setConfidenceStart(parseInt(e.target.value, 10))}
                  data-testid="confidence-start-slider"
                  className="w-full accent-gold cursor-pointer" />
                <div className="flex justify-between font-dm text-navy/40 text-[10px] mt-1">
                  <span>1 · Not confident at all</span>
                  <span className="font-playfair text-navy text-2xl font-bold" data-testid="confidence-start-value">{confidenceStart}</span>
                  <span>10 · Fully confident</span>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(2)} className="font-dm text-navy/60 px-4 py-3 hover:text-navy">← Back</button>
                <button onClick={() => setStep(4)} data-testid="step3-next" className="flex-1 bg-navy text-gold font-dm font-bold py-3 rounded-lg">Next</button>
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {step === 4 && (
            <div data-testid="coaching-step-4">
              <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Step 4 — Ready when you are</h2>
              {freeAvailable && <p className="font-dm text-green-700 text-sm font-bold mb-4">✓ Starting your free session</p>}
              <div className="bg-cream rounded-xl p-5 mb-5">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg" style={{ backgroundColor: selectedCoach?.bg, color: selectedCoach?.fg }}>{selectedCoach?.initials}</div>
                  <div>
                    <p className="font-playfair text-navy text-lg font-bold">{selectedCoach?.name}</p>
                    <p className="font-dm text-navy/50 text-xs">{selectedCoach?.flag} {selectedCoach?.origin} · {selectedCoach?.level}</p>
                  </div>
                </div>
                <div className="space-y-1 font-dm text-sm">
                  <p className="text-navy/70"><span className="text-navy/40">Topic:</span> <strong className="text-navy">{finalTopic}</strong></p>
                  <p className="text-navy/70"><span className="text-navy/40">Length:</span> <strong className="text-navy">{length} minutes</strong></p>
                  <p className="text-navy/70"><span className="text-navy/40">Cost:</span> <strong className={freeAvailable ? "text-green-700" : "text-gold"}>{freeAvailable ? "FREE (first session)" : `${cost} assessment tokens`}</strong></p>
                </div>
              </div>
              {error && <p className="font-dm text-red-600 text-sm mb-3" data-testid="start-error">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => setStep(3)} className="font-dm text-navy/60 px-4 py-3 hover:text-navy">← Back</button>
                <button onClick={startSession} disabled={starting} data-testid="begin-session-btn" className="flex-1 bg-gold text-navy font-dm font-bold py-3 rounded-lg disabled:opacity-50">
                  {starting ? "Starting..." : "Begin Session →"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
